/* eslint-disable @typescript-eslint/no-explicit-any */
import Modal from "../../Components/Modal";
import { Vocabulary } from "../../Utils/Vocabulary";
import {
  TextField,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import SaveIcon from "@mui/icons-material/Save";
import { UrlEnum } from "../../Utils/Utils";
import { postData } from "../../Services/postData";
import { toast } from "react-toastify";
import { updateData } from "../../Services/updateData";
import { getData } from "../../Services/getData";
import theme from "../../Theme/Theme";
import { DropzoneArea } from "material-ui-dropzone";

type DocumentTemplateProps = {
  open: boolean;
  onClose: (shouldRefetch?: boolean) => void;
  data: any;
};

export type DocumentTemplateState = {
  _id: string | null;
  name: string;
  description: string;
  documentType: string;
  file: string | null;
  originalFileName: string | null;
};

const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export default function DocumentTemplate({ open, onClose, data }: DocumentTemplateProps) {
  const [state, setState] = useState<DocumentTemplateState>({
    _id: null,
    name: "",
    description: "",
    documentType: "",
    file: null,
    originalFileName: null,
  });
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<any>(null);
  const [documentTypes, setDocumentTypes] = useState<string[]>([]);
  const notifySuccess = (message: string) => toast.success(message);
  const notifyError = (message: string) => toast.error(message);

  useEffect(() => {
    if (data) {
      setState(data);
    }
    const excludeId = data?._id ? `?excludeId=${data._id}` : "";
    getData(`${UrlEnum.documentTemplates}/documentTypes${excludeId}`)
      .then((res: any) => setDocumentTypes(res.data || []))
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * save document template
   */
  function saveDocumentTemplate() {
    if (!state.name.trim()) {
      notifyError(Vocabulary.requiredField);
      return;
    }
    if (!state.documentType) {
      notifyError(Vocabulary.requiredField);
      return;
    }
    if (!file && !state._id) {
      notifyError(Vocabulary.documentFile);
      return;
    }
    setLoading(true);
    let url = UrlEnum.documentTemplates;
    const formData = new FormData();
    formData.append("name", state.name);
    formData.append("description", state.description || "");
    formData.append("documentType", state.documentType);
    if (state.file) formData.append("file", state.file);
    if (file) formData.append("file", file, file.name);
    if (state._id) {
      url = `${url}/${state._id}`;
      updateData(url, formData)
        .then(() => {
          onClose(true);
          notifySuccess(Vocabulary.saveSuccess);
          setLoading(false);
        })
        .catch(() => {
          notifyError(Vocabulary.saveError);
          setLoading(false);
        });
    } else {
      postData(url, formData)
        .then(() => {
          onClose(true);
          notifySuccess(Vocabulary.saveSuccess);
          setLoading(false);
        })
        .catch(() => {
          notifyError(Vocabulary.saveError);
          setLoading(false);
        });
    }
  }

  /**
   *
   * @param files
   */
  function handleChangeDropFile(files: any) {
    if (files.length !== 0) {
      if (files[0].type === DOCX_MIME || files[0].name.toLowerCase().endsWith(".docx")) {
        setFile(files[0]);
      } else {
        notifyError(Vocabulary.documentFile);
      }
    }
  }

  return (
    <>
      <Modal
        title={Vocabulary.newDocumentTemplate}
        open={open}
        onClose={() => onClose(false)}
        restModalProps={{
          maxWidth: "xl",
        }}
      >
        <br />
        <TextField
          fullWidth
          size="small"
          label={Vocabulary.name}
          value={state.name}
          onChange={(event: any) => setState({ ...state, name: event.target.value })}
        />
        <TextField
          fullWidth
          multiline
          rows={3}
          size="small"
          style={{ marginTop: 20 }}
          label={Vocabulary.description}
          value={state.description}
          onChange={(event: any) => setState({ ...state, description: event.target.value })}
        />
        <FormControl fullWidth size="small" style={{ marginTop: 20 }}>
          <InputLabel>{Vocabulary.documentType}</InputLabel>
          <Select
            label={Vocabulary.documentType}
            value={state.documentType}
            onChange={(event: any) =>
              setState({ ...state, documentType: event.target.value })
            }
          >
            {documentTypes.map((documentType) => (
              <MenuItem key={documentType} value={documentType}>
                {documentType}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {state.originalFileName ? (
          <div style={{ textAlign: "center", marginTop: 20 }}>
            <Typography variant="body2" color="textSecondary">
              {Vocabulary.currentFile}
            </Typography>
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              {state.originalFileName}
            </Typography>
          </div>
        ) : (
          ""
        )}
        <div style={{ marginTop: 10 }}>
          <DropzoneArea
            filesLimit={1}
            dropzoneText={Vocabulary.documentFile}
            onChange={handleChangeDropFile}
            dropzoneClass="custom-dropzone"
            showPreviewsInDropzone={false}
            showFileNames
          />
        </div>
        {file ? (
          <div style={{ textAlign: "center", marginTop: 10 }}>
            <Typography variant="body2" color="textSecondary">
              {Vocabulary.newFileSelected}
            </Typography>
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              {file.name}
            </Typography>
          </div>
        ) : (
          ""
        )}
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <Button
            variant="contained"
            size="large"
            style={{
              color: theme.palette.whiteColor?.main,
              backgroundColor: theme.palette.primary?.main,
            }}
            color="primary"
            onClick={saveDocumentTemplate}
          >
            <SaveIcon /> &nbsp;&nbsp;{Vocabulary.save}
          </Button>
        </div>
      </Modal>

      {loading ? (
        <div className="loading">
          <CircularProgress color="primary" />
        </div>
      ) : (
        ""
      )}
    </>
  );
}
